import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { getDuplicateApps } from "go-app/utils";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { getTimezone } from "go-core/utils/utils";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { ConfigContext } from "../../../../../../../../services/Config/context";
import { SupportAccessOrganizationApi } from "../../../../../../../../services/support/types";
import { getNewAppsVersion } from "../../../../../../../../utils/AppVersion";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "SUPPORT_ORGANIZATION";
const resourceType = "SUPPORT_ORGANIZATION";

const List = ({ resource }: ListState) => {
	const configService = useContext(ConfigContext);
	const segmentContextValue = React.useContext(SegmentContext);
	const { t } = useTranslation("admin");
	const applicationsFromConfig = [...configService.get().apps];
	const duplicateApplications = getDuplicateApps(applicationsFromConfig);
	const filteredApplications = getNewAppsVersion(duplicateApplications, applicationsFromConfig);

	if (!resource) return null;
	const data = resource.read();

	let config = {
		fields: [
			{
				id: "name",
				title: t("lib:common.word.name"),
			},
			{
				id: "company_id",
				name: t("common.word.company_id"),
				type: "text",
			},
			{
				id: "applications",
				name: t("common.word.applications"),
				render: (item: SupportAccessOrganizationApi) => {
					const applications = item.applications;
					return applications.map((app, index) => {
						const appFromConfig = filteredApplications.find(
							(uniqueApplication) => uniqueApplication.id.toUpperCase() === app.slug
						);
						const redirectURL = appFromConfig?.url;

						return (
							<div className="d-flex flex-column" key={`application-${index}`}>
								<a href={redirectURL}>{app.name}</a>
								<span>
									{t("modules.support.field.support_access_to.title")}
									<span className="ms-1 text-muted">
										{FormatDate(app.support_access_to, getTimezone())}
									</span>
								</span>
								<RenderLimitedText minWidth={200}>
									<span>
										{t("modules.support.field.support_access_permissions.title")}
										<span className="ms-1 text-muted">
											{app.support_access_permissions.join(", ")}
										</span>
									</span>
								</RenderLimitedText>
							</div>
						);
					});
				},
			},
		],
		filters: [
			{
				id: "tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "email",
				name: t("lib:common.word.email"),
				type: "text",
			},
			{
				id: "phone_number",
				name: t("lib:common.word.phone_number"),
				type: "text",
			},
			{
				id: "organization_name",
				name: t("modules.support.field.organization_name.title"),
				type: "text",
			},
			{
				id: "company_name",
				name: t("common.word.company_name"),
				type: "text",
			},
			{
				id: "user_name",
				name: t("modules.support.field.user_name.title"),
				type: "text",
			},
		],
		selectedColumns: ["name", "company_id", "applications"],
		actions: [
			{
				name: t("lib:common.action.goto"),
				link: (item: SupportAccessOrganizationApi) => `/${item.id}`,
			},
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: SupportAccessOrganizationApi) => `/support/organizations_with_access/${item.id}`,
			},
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "applications";
			return adminApi.getSupportAccessOrganizations(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.support.field.organizations.title"),
			filename: t("modules.support.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
		filterValues: data.filter_values,
	};

	return <ListData config={config} />;
};

const SupportOrganizationsIndexPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.support.field.organizations.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.support.field.organizations.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};

export default SupportOrganizationsIndexPage;
